import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronRight, ChevronDown, TrendingUp, TrendingDown, Download } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import DateFilter from "@/components/DateFilter";
import { downloadExcel } from "@/lib/download";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);
  // Accounting convention: negative amounts displayed in parentheses
  return amount < 0 ? `(${formatted})` : formatted;
}

function GroupSection({ items, isExpense }) {
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (name) => {
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return items.map((group, gIdx) => {
    const isExpanded = expandedGroups[group.name];
    const hasChildren = group.children && group.children.length > 0;

    return (
      <React.Fragment key={group.name || gIdx}>
        <TableRow
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => hasChildren && toggleGroup(group.name)}
          data-testid={`pl-row-${group.name?.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <TableCell className="font-semibold" style={{ paddingLeft: "12px" }}>
            <div className="flex items-center gap-2">
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )
              ) : (
                <span className="w-4" />
              )}
              <span>{group.name}</span>
            </div>
          </TableCell>
          <TableCell className="text-right font-mono tabular-nums">
            <span className={isExpense ? "text-red-600" : "text-green-600"}>
              {formatCurrency(group.amount)}
            </span>
          </TableCell>
        </TableRow>
        {isExpanded &&
          hasChildren &&
          group.children.map((child, cIdx) => (
            <TableRow
              key={`${gIdx}-${cIdx}`}
              className="hover:bg-slate-50"
              data-testid={`pl-child-${child.name?.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <TableCell style={{ paddingLeft: "44px" }}>
                <span className={child.is_group ? "font-semibold text-sm" : "text-sm"}>
                  {child.name}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm">
                <span className={isExpense ? "text-red-600" : "text-green-600"}>
                  {formatCurrency(child.amount)}
                </span>
              </TableCell>
            </TableRow>
          ))}
      </React.Fragment>
    );
  });
}

export default function ProfitLoss({ companyContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});

  const companyId = companyContext?.companyId || "default";
  const branchId = companyContext?.branchId || null;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { ...filters, company_id: companyId };
        if (branchId) params.branch_id = branchId;
        const response = await axios.get(`${API}/reports/profit-loss`, { params });
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, companyId, branchId]);

  if (error) {
    return (
      <div className="text-center py-8 text-red-600" data-testid="pl-error">
        {error}
      </div>
    );
  }

  const netProfit = data?.net_profit || 0;
  const isProfit = netProfit >= 0;

  return (
    <div data-testid="profit-loss">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="pl-title">
            Profit & Loss Statement
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Click on any group to expand details
          </p>
        </div>
        <Button
          onClick={() => downloadExcel(
            `${API}/reports/profit-loss/export`,
            { ...filters, company_id: companyId, ...(branchId ? { branch_id: branchId } : {}) },
            `Profit_Loss_${new Date().toISOString().slice(0, 10)}.xlsx`,
          )}
          variant="outline"
          className="border-slate-300"
          data-testid="pl-download"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Excel
        </Button>
      </div>

      <DateFilter onFilterChange={setFilters} />

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="pl-loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Income</p>
            <p className="text-xl font-bold text-green-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data?.total_income)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Expenses</p>
            <p className="text-xl font-bold text-red-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data?.total_expense)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {isProfit ? "Net Profit" : "Net Loss"}
                </p>
                <p className={`text-xl font-bold mt-1 font-mono tabular-nums ${isProfit ? "text-green-600" : "text-red-600"}`}>
                  &#8377; {formatCurrency(netProfit)}
                </p>
              </div>
              {isProfit ? (
                <TrendingUp className="w-8 h-8 text-green-500" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-slate-200 shadow-none mb-6">
        <CardHeader className="pb-2 bg-green-50">
          <CardTitle className="text-sm font-semibold text-green-800">Income</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="income-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Account</TableHead>
                <TableHead className="text-right font-semibold w-40">Amount (&#8377;)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <GroupSection items={data?.income || []} isExpense={false} />
              {(!data?.income || data.income.length === 0) && (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                    No income data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-2 bg-red-50">
          <CardTitle className="text-sm font-semibold text-red-800">Expenses</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="expense-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Account</TableHead>
                <TableHead className="text-right font-semibold w-40">Amount (&#8377;)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <GroupSection items={data?.expenses || []} isExpense={true} />
              {(!data?.expenses || data.expenses.length === 0) && (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                    No expense data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
}
